/**
 * ManufacturerDropdown Component
 * A dropdown for selecting manufacturers from CARA registration data.
 * Shows all options on focus, supports typeahead filtering, and includes
 * a "Value not in list" fallback option.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

export interface ManufacturerOption {
  value: string;
  source: string;
}

export interface ManufacturerDropdownProps {
  label: string;
  value: string;
  options: ManufacturerOption[];
  onChange: (opt: ManufacturerOption) => void;
  onNotFound?: () => void;
  placeholder?: string;
}

export const NOT_FOUND_VALUE = 'Value not in list';

export function ManufacturerDropdown({
  label,
  value,
  options,
  onChange,
  onNotFound,
  placeholder = 'Select from list',
}: ManufacturerDropdownProps) {
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasSelection, setHasSelection] = useState(!!value);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHasSelection(!!value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setSearchText('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = useMemo(
    () => searchText
      ? options.filter(opt => opt.value.toLowerCase().includes(searchText.toLowerCase()))
      : options,
    [searchText, options]
  );

  // Display only the manufacturer name (before the first comma), not the location
  const nameOnly = useCallback((val: string) => val.split(',')[0].trim(), []);

  const displayValue = showDropdown
    ? searchText
    : hasSelection ? nameOnly(value) : '';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setSearchText(text);
    setShowDropdown(true);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onFocus={() => {
          setSearchText('');
          setShowDropdown(true);
        }}
        placeholder={placeholder}
        className={`w-full px-3 py-2 border border-gray-200 rounded focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400 ${
          !showDropdown && !hasSelection ? 'text-sm italic text-gray-400' : ''
        }`}
      />
      {showDropdown && (
        <div
          className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          onMouseDown={(e) => e.preventDefault()}
        >
          {filteredOptions.map((option) => (
            <div
              key={option.value}
              onClick={(e) => {
                e.stopPropagation();
                onChange(option);
                setHasSelection(true);
                setSearchText('');
                setShowDropdown(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-green-50 transition-colors border-b border-gray-100 last:border-b-0 cursor-pointer"
            >
              <span className="text-sm font-medium">{option.value.split(',')[0].trim()}</span>
              {option.value.includes(',') && (
                <span className="text-xs text-gray-400 ml-2">{option.value.substring(option.value.indexOf(',') + 1).trim()}</span>
              )}
            </div>
          ))}
          <div
            onClick={(e) => {
              e.stopPropagation();
              onChange({ value: NOT_FOUND_VALUE, source: 'Manual' });
              setHasSelection(true);
              setSearchText('');
              setShowDropdown(false);
              if (onNotFound) onNotFound();
            }}
            className="w-full text-left px-3 py-2 hover:bg-green-50 transition-colors cursor-pointer border-t border-gray-200"
          >
            <span className="text-sm italic text-gray-500">Value not in list</span>
          </div>
        </div>
      )}
    </div>
  );
}
